import React, { useCallback, useEffect, useState } from "react";
import { Trash2, RotateCcw, Folder, FileText, Image as ImageIcon, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { dapi as api } from "./deskApi";
import { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { fileIcon } from "./fileIcon";

const itemIcon = (n) => fileIcon(n).Icon;

/** Recycle Bin — soft-deleted File Manager / Desktop items. Items are
 * purged automatically 30 days after deletion. */
export default function RecycleBinApp() {
  const [items, setItems] = useState([]);
  const [retention, setRetention] = useState(30);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/desktop/trash");
      setItems(data.items || []);
      setRetention(data.retention_days || 30);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    window.addEventListener("desk-fs-changed", load);
    return () => window.removeEventListener("desk-fs-changed", load);
  }, [load]);

  const restore = async (it) => {
    setBusy(true);
    try {
      await api.post(`/desktop/trash/${it.id}/restore`);
      toast.success(`Restored "${it.name}".`);
      window.dispatchEvent(new CustomEvent("desk-fs-changed"));
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const purge = async (it) => {
    if (!window.confirm(`Permanently delete "${it.name}"? This can't be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/desktop/fs/${it.id}`);
      window.dispatchEvent(new CustomEvent("desk-fs-changed"));
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const emptyBin = async () => {
    if (!window.confirm("Permanently delete EVERYTHING in the Recycle Bin? This can't be undone.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/desktop/trash/empty");
      toast.success(`Recycle Bin emptied (${data.deleted} item${data.deleted === 1 ? "" : "s"}).`);
      window.dispatchEvent(new CustomEvent("desk-fs-changed"));
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="h-full flex flex-col bg-white" data-testid="recycle-bin-app">
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b bg-slate-50">
        <div className="text-xs text-slate-500">
          Items are deleted automatically after {retention} days.
        </div>
        <div className="flex items-center gap-1.5">
          <button type="button" onClick={load} className="p-1.5 rounded hover:bg-slate-200 text-slate-600" title="Refresh" data-testid="recycle-refresh">
            <RefreshCw className="w-4 h-4" />
          </button>
          <Button
            variant="destructive"
            size="sm"
            className="rounded-sm h-8"
            onClick={emptyBin}
            disabled={busy || items.length === 0}
            data-testid="recycle-empty-btn"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Empty Recycle Bin
          </Button>
        </div>
      </div>
      <div className="flex-1 overflow-auto">
        {loading ? (
          <p className="text-sm text-slate-500 p-4">Loading…</p>
        ) : items.length === 0 ? (
          <div className="h-full grid place-items-center text-center p-6" data-testid="recycle-empty-state">
            <div>
              <Trash2 className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">The Recycle Bin is empty.</p>
            </div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b">
                <th className="px-3 py-2 font-semibold">Name</th>
                <th className="px-3 py-2 font-semibold">Deleted</th>
                <th className="px-3 py-2 font-semibold">Auto-delete in</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const { Icon, color } = fileIcon(it);
                return (
                  <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`recycle-item-${it.id}`}>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-2 min-w-0">
                        <Icon className={`w-4 h-4 shrink-0 ${color}`} />
                        <span className="truncate max-w-[220px]" title={it.name}>{it.name}</span>
                      </span>
                    </td>
                    <td className="px-3 py-2 text-slate-500 whitespace-nowrap">
                      {it.deleted_at ? new Date(it.deleted_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className={`text-xs font-semibold ${it.days_left <= 5 ? "text-red-600" : "text-slate-600"}`}>
                        {it.days_left} day{it.days_left === 1 ? "" : "s"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => restore(it)}
                        disabled={busy}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:underline mr-3 disabled:opacity-50"
                        data-testid={`recycle-restore-${it.id}`}
                      >
                        <RotateCcw className="w-3.5 h-3.5" /> Restore
                      </button>
                      <button
                        type="button"
                        onClick={() => purge(it)}
                        disabled={busy}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:underline disabled:opacity-50"
                        data-testid={`recycle-purge-${it.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
